/**
 * Country/region code → geographic coordinates lookup.
 *
 * Used to place community hotspots on the globe when CommunityEntity
 * has a `code` field (e.g. "FR", "US-TX") but no explicit lat/lon.
 * Coordinates point to capital cities or geographic centroids.
 *
 * For sub-country entities (cities, states, regions), if no direct match
 * is found, walks up the parent hierarchy via communityRelationships
 * until a country-level entity is found, then uses that country's coords.
 *
 * Expand the lookup tables as new geographies are added.
 */

export interface GeoCoord {
  lat: number;
  lon: number;
}

/** ISO 3166-1 alpha-2 country codes → representative coordinates. */
const COUNTRY_COORDS: Record<string, GeoCoord> = {
  // ── Americas ──
  US: { lat: 38.9, lon: -77.04 },    // Washington D.C.
  CA: { lat: 45.42, lon: -75.7 },     // Ottawa
  MX: { lat: 19.43, lon: -99.13 },    // Mexico City
  BR: { lat: -15.79, lon: -47.88 },   // Brasília
  AR: { lat: -34.6, lon: -58.38 },    // Buenos Aires
  CO: { lat: 4.71, lon: -74.07 },     // Bogotá
  CL: { lat: -33.45, lon: -70.67 },   // Santiago
  PE: { lat: -12.05, lon: -77.04 },   // Lima

  // ── Europe ──
  GB: { lat: 51.51, lon: -0.13 },     // London
  FR: { lat: 48.86, lon: 2.35 },      // Paris
  DE: { lat: 52.52, lon: 13.41 },     // Berlin
  IT: { lat: 41.9, lon: 12.5 },       // Rome
  ES: { lat: 40.42, lon: -3.7 },      // Madrid
  NL: { lat: 52.37, lon: 4.9 },       // Amsterdam
  BE: { lat: 50.85, lon: 4.35 },      // Brussels
  CH: { lat: 46.95, lon: 7.45 },      // Bern
  SE: { lat: 59.33, lon: 18.07 },     // Stockholm
  NO: { lat: 59.91, lon: 10.75 },     // Oslo
  PL: { lat: 52.23, lon: 21.01 },     // Warsaw
  PT: { lat: 38.72, lon: -9.14 },     // Lisbon
  AT: { lat: 48.21, lon: 16.37 },     // Vienna
  IE: { lat: 53.35, lon: -6.26 },     // Dublin
  GR: { lat: 37.98, lon: 23.73 },     // Athens
  FI: { lat: 60.17, lon: 24.94 },     // Helsinki
  DK: { lat: 55.68, lon: 12.57 },     // Copenhagen
  UA: { lat: 50.45, lon: 30.52 },     // Kyiv
  RO: { lat: 44.43, lon: 26.1 },      // Bucharest
  CZ: { lat: 50.08, lon: 14.44 },     // Prague

  // ── Asia ──
  JP: { lat: 35.68, lon: 139.69 },    // Tokyo
  CN: { lat: 39.9, lon: 116.41 },     // Beijing
  IN: { lat: 28.61, lon: 77.21 },     // New Delhi
  KR: { lat: 37.57, lon: 126.98 },    // Seoul
  SG: { lat: 1.35, lon: 103.82 },     // Singapore
  TH: { lat: 13.76, lon: 100.5 },     // Bangkok
  VN: { lat: 21.03, lon: 105.85 },    // Hanoi
  PH: { lat: 14.6, lon: 120.98 },     // Manila
  ID: { lat: -6.21, lon: 106.85 },    // Jakarta
  MY: { lat: 3.14, lon: 101.69 },     // Kuala Lumpur
  PK: { lat: 33.69, lon: 73.04 },     // Islamabad
  BD: { lat: 23.81, lon: 90.41 },     // Dhaka
  TW: { lat: 25.03, lon: 121.57 },    // Taipei

  // ── Middle East ──
  IL: { lat: 31.78, lon: 35.24 },     // Jerusalem
  AE: { lat: 24.45, lon: 54.65 },     // Abu Dhabi
  SA: { lat: 24.71, lon: 46.68 },     // Riyadh
  TR: { lat: 39.93, lon: 32.86 },     // Ankara
  QA: { lat: 25.29, lon: 51.53 },     // Doha

  // ── Africa ──
  ZA: { lat: -25.75, lon: 28.19 },    // Pretoria
  NG: { lat: 9.06, lon: 7.49 },       // Abuja
  KE: { lat: -1.29, lon: 36.82 },     // Nairobi
  EG: { lat: 30.04, lon: 31.24 },     // Cairo
  GH: { lat: 5.56, lon: -0.19 },      // Accra
  ET: { lat: 9.01, lon: 38.75 },      // Addis Ababa
  MA: { lat: 33.97, lon: -6.85 },     // Rabat
  TZ: { lat: -6.16, lon: 35.75 },     // Dodoma

  // ── Oceania ──
  AU: { lat: -35.28, lon: 149.13 },   // Canberra
  NZ: { lat: -41.29, lon: 174.78 },   // Wellington
};

/** US state codes → representative coordinates. */
const US_STATE_COORDS: Record<string, GeoCoord> = {
  "US-TX": { lat: 30.27, lon: -97.74 },  // Austin
  "US-CA": { lat: 38.58, lon: -121.49 }, // Sacramento
  "US-NY": { lat: 40.71, lon: -74.01 },  // New York City
  "US-FL": { lat: 30.44, lon: -84.28 },  // Tallahassee
  "US-IL": { lat: 41.88, lon: -87.63 },  // Chicago
  "US-PA": { lat: 39.95, lon: -75.16 },  // Philadelphia
  "US-OH": { lat: 39.96, lon: -82.99 },  // Columbus
  "US-GA": { lat: 33.75, lon: -84.39 },  // Atlanta
  "US-MA": { lat: 42.36, lon: -71.06 },  // Boston
  "US-WA": { lat: 47.61, lon: -122.33 }, // Seattle
  "US-CO": { lat: 39.74, lon: -104.99 }, // Denver
  "US-VA": { lat: 37.54, lon: -77.44 },  // Richmond
  "US-NC": { lat: 35.78, lon: -78.64 },  // Raleigh
  "US-AZ": { lat: 33.45, lon: -112.07 }, // Phoenix
  "US-MI": { lat: 42.33, lon: -83.05 },  // Detroit
  "US-MN": { lat: 44.98, lon: -93.27 },  // Minneapolis
  "US-OR": { lat: 45.52, lon: -122.68 }, // Portland
  "US-TN": { lat: 36.16, lon: -86.78 },  // Nashville
  "US-MO": { lat: 38.63, lon: -90.2 },   // St. Louis
  "US-MD": { lat: 39.29, lon: -76.61 },  // Baltimore
  "US-WI": { lat: 43.04, lon: -87.91 },  // Milwaukee
  "US-LA": { lat: 29.95, lon: -90.07 },  // New Orleans
  "US-HI": { lat: 21.31, lon: -157.86 }, // Honolulu
  "US-DC": { lat: 38.9, lon: -77.04 },   // Washington D.C.
};

/** Well-known city names → coordinates (fallback for name-based matching). */
const CITY_COORDS: Record<string, GeoCoord> = {
  houston: { lat: 29.76, lon: -95.37 },
  "new york": { lat: 40.71, lon: -74.01 },
  "los angeles": { lat: 34.05, lon: -118.24 },
  chicago: { lat: 41.88, lon: -87.63 },
  london: { lat: 51.51, lon: -0.13 },
  paris: { lat: 48.86, lon: 2.35 },
  tokyo: { lat: 35.68, lon: 139.69 },
  sydney: { lat: -33.87, lon: 151.21 },
  dubai: { lat: 25.2, lon: 55.27 },
  mumbai: { lat: 19.08, lon: 72.88 },
  shanghai: { lat: 31.23, lon: 121.47 },
  "são paulo": { lat: -23.55, lon: -46.63 },
  nairobi: { lat: -1.29, lon: 36.82 },
  berlin: { lat: 52.52, lon: 13.41 },
  rome: { lat: 41.9, lon: 12.5 },
  madrid: { lat: 40.42, lon: -3.7 },
  toronto: { lat: 43.65, lon: -79.38 },
  singapore: { lat: 1.35, lon: 103.82 },
  seoul: { lat: 37.57, lon: 126.98 },
  amsterdam: { lat: 52.37, lon: 4.9 },
};

/** Planet-level fallback (center of Atlantic — visible on default globe rotation). */
const PLANET_COORD: GeoCoord = { lat: 20, lon: -30 };

/** Common country name → ISO code mapping for name-based fallback. */
const NAME_TO_CODE: Record<string, string> = {
  "united states": "US", usa: "US", america: "US",
  canada: "CA",
  mexico: "MX",
  brazil: "BR",
  argentina: "AR",
  colombia: "CO",
  chile: "CL",
  peru: "PE",
  "united kingdom": "GB", uk: "GB", england: "GB", britain: "GB",
  france: "FR",
  germany: "DE",
  italy: "IT",
  spain: "ES",
  netherlands: "NL", holland: "NL",
  belgium: "BE",
  switzerland: "CH",
  sweden: "SE",
  norway: "NO",
  poland: "PL",
  portugal: "PT",
  austria: "AT",
  ireland: "IE",
  greece: "GR",
  finland: "FI",
  denmark: "DK",
  ukraine: "UA",
  romania: "RO",
  "czech republic": "CZ", czechia: "CZ",
  japan: "JP",
  china: "CN",
  india: "IN",
  "south korea": "KR", korea: "KR",
  singapore: "SG",
  thailand: "TH",
  vietnam: "VN",
  philippines: "PH",
  indonesia: "ID",
  malaysia: "MY",
  pakistan: "PK",
  bangladesh: "BD",
  taiwan: "TW",
  israel: "IL",
  "united arab emirates": "AE", uae: "AE",
  "saudi arabia": "SA",
  turkey: "TR", türkiye: "TR",
  qatar: "QA",
  "south africa": "ZA",
  nigeria: "NG",
  kenya: "KE",
  egypt: "EG",
  ghana: "GH",
  ethiopia: "ET",
  morocco: "MA",
  tanzania: "TZ",
  australia: "AU",
  "new zealand": "NZ",
};

/** Entity types that are at or above the country level. */
const COUNTRY_LEVEL_TYPES = new Set([
  'geo_country',
  'geo_continent',
  'geo_planet',
]);

/**
 * Attempt to resolve geographic coordinates for a community entity.
 *
 * Resolution order:
 * 1. Entity's `code` field matched against country/state tables
 * 2. Entity's `name` field matched against city table (case-insensitive)
 * 3. Country name → ISO code → coordinates
 * 4. Entity type "geo_planet" → center-of-view fallback
 * 5. Walk up the parent hierarchy to find a country ancestor
 * 6. null if nothing matched
 *
 * @param entity      The community entity to resolve
 * @param entities    All community entities (for parent walking)
 * @param relationships  Community relationship records (child→parent)
 */
export function resolveEntityCoords(
  entity: Record<string, any>,
  entities?: Record<string, any>[],
  relationships?: Record<string, any>[],
): GeoCoord | null {
  const result = resolveDirectCoords(entity);
  if (result) return result;

  // Walk up parent hierarchy to find a country-level ancestor
  if (entities && relationships) {
    const parentCoords = walkToCountry(entity, entities, relationships);
    if (parentCoords) return parentCoords;
  }

  return null;
}

/**
 * Try to resolve coordinates directly from the entity's own data.
 */
function resolveDirectCoords(entity: Record<string, any>): GeoCoord | null {
  const code = (entity.code || entity.entity_code || "").toUpperCase().trim();
  const name = (entity.name || entity.entity_name || "").toLowerCase().trim();
  const entityType = (entity.entityType || entity.entity_type || "").toLowerCase();

  // 1. Code-based lookup (most reliable)
  if (code) {
    if (US_STATE_COORDS[code]) return US_STATE_COORDS[code];
    if (COUNTRY_COORDS[code]) return COUNTRY_COORDS[code];
  }

  // 2. City name lookup
  if (name && CITY_COORDS[name]) return CITY_COORDS[name];

  // 3. Country name → ISO code
  if (name) {
    const nameToCode = NAME_TO_CODE[name];
    if (nameToCode && COUNTRY_COORDS[nameToCode]) return COUNTRY_COORDS[nameToCode];
  }

  // 4. Planet-level entity
  if (entityType === "geo_planet") return PLANET_COORD;

  return null;
}

/**
 * Walk the parent hierarchy from a sub-country entity up to a country.
 * Uses communityRelationships (child_entity_guid → parent_entity_guid).
 * Stops after 5 hops to prevent infinite loops.
 */
function walkToCountry(
  entity: Record<string, any>,
  entities: Record<string, any>[],
  relationships: Record<string, any>[],
  maxHops: number = 5,
): GeoCoord | null {
  // Build child→parent map
  const parentMap = new Map<string, string>();
  for (const rel of relationships) {
    const child = (rel.child_entity_guid || rel.childEntityGuid || "").toUpperCase();
    const parent = (rel.parent_entity_guid || rel.parentEntityGuid || "").toUpperCase();
    if (child && parent) parentMap.set(child, parent);
  }

  // Build guid→entity map
  const entityMap = new Map<string, Record<string, any>>();
  for (const e of entities) {
    const guid = (e.entity_guid || e.entityGuid || e.id || "").toUpperCase();
    if (guid) entityMap.set(guid, e);
  }

  // Walk up
  let currentGuid = (entity.entity_guid || entity.entityGuid || entity.id || "").toUpperCase();
  for (let i = 0; i < maxHops; i++) {
    const parentGuid = parentMap.get(currentGuid);
    if (!parentGuid) break;

    const parentEntity = entityMap.get(parentGuid);
    if (!parentEntity) break;

    // Check if this parent resolves to coordinates
    const coords = resolveDirectCoords(parentEntity);
    if (coords) return coords;

    currentGuid = parentGuid;
  }

  return null;
}
